/**
 * @mergeTarget
 * @module Src/Categories
 */
import { LisioBooleanParameterNames } from "../parameter/lisio-boolean-parameter.js";
import { LisioNumericParameterNames } from "../parameter/lisio-numeric-parameter.js";
import { LisioStringParameterNames } from "../parameter/lisio-string-parameter.js";
import { LisioProfilErrorCodes } from "../profil-error-codes.js";
import { LisioProfileNames } from "../profile/lisio-profile.js";
import { LisioCategory, LisioCategoryNames } from "./lisio-category.js";
/**
 * ## How to create a new category ? Part 2
 *
 * To create a new category is very easy, if you don't create a new category name go to {@link Src/Categories.LisioCategoryNames | LisioCategoryNames}.\
 * Next go create a new builder for your category, it only consists at choose which parameters, profiles and categories your category will include. You can take exemple on other builders.\
 * When it's done the final step is to add your new builder in {@link LisioCategoryFactory._builders | LisioCategoryFactory._builders}
 *
 * ## How to modify a category ? Part 2
 *
 * To rename a category is very easy, if you want to rename your category and you don't choose a new category name go to {@link Src/Categories.LisioCategoryNames | LisioCategoryNames}.\
 * Next change all old category name by your new category name.\
 *
 * If you want to modify parameters, profiles or subcategories used in category, go to category builder and modify it as you desire.
 *
 * ## How to delete a category ? Part 2
 *
 * To delete a category is very easy, if you don't delete category name go to {@link Src/Categories.LisioCategoryNames | LisioCategoryNames}.\
 * Next delete your category builder.\
 * When it's done the final step is to remove your old builder from {@link LisioCategoryFactory._builders | LisioCategoryFactory._builders}
 *
 * ## Documentation
 *
 * Class representing a category factory.\
 * It aims to centralize all instanciation of category.\
 * To execute his responsability this class does :
 *  * Build a category with right corresponding parameters, categorys and subcategories
 */
class LisioCategoryFactory {
    /**
     * Private attribute to store all category builders
     * @source
     */
    _builders = new Map([
        [LisioCategoryNames.ACCESSIBILITY, this.accessibilityBuilder],
        [LisioCategoryNames.COMFORT, this.comfortBuilder],
        [LisioCategoryNames.GOOGLE_TRANSLATION, this.googleTranslationBuilder],
        [LisioCategoryNames.DEEPL_TRANSLATION, this.deeplTranslationBuilder],
        [LisioCategoryNames.SPEECH_SYNTHESIS, this.speechSynthesisBuilder],
        [LisioCategoryNames.RURAL, this.ruralBuilder],
        [LisioCategoryNames.ECOLOGICAL, this.ecologicalBuilder],
        [LisioCategoryNames.LIGHT_ECOLOGICAL, this.lightEcologicalBuilder],
        [LisioCategoryNames.DALTONISM, this.daltonismBuilder],
        [LisioCategoryNames.DYSLEXIA_COMFORT, this.dyslexiaBuilder],
        [LisioCategoryNames.SETTINGS, this.settingsBuilder],
    ]);
    /**
     * @static
     * Private attribute to store instance of {@link LisioCategoryFactory | LisioCategoryFactory}
     * @source
     */
    static _current;
    /**
     * @static
     * Getter for attribute {@link _current | _current}
     * @returns Returns _current attribute
     * @source
     */
    static get current() {
        return LisioCategoryFactory._current;
    }
    /**
     * Private method to build light ecological category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns light ecological category
     * @source
     */
    lightEcologicalBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const lightEcologicalProfiles = [
            LisioProfileNames.LIGHT_ECOLO,
        ].filter(profile => !profilesToHide.includes(profile));
        return new LisioCategory(LisioCategoryNames.LIGHT_ECOLOGICAL, lightEcologicalProfiles, [].filter(parameter => !parametersToHide.includes(parameter)), []);
    }
    /**
     * Private method to build ecological category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns ecological category
     * @source
     */
    ecologicalBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const ecologicalProfiles = [
            LisioProfileNames.ECOLO,
            LisioProfileNames.LIGHT_ECOLO,
        ].filter(profile => !profilesToHide.includes(profile));
        return new LisioCategory(LisioCategoryNames.ECOLOGICAL, ecologicalProfiles, [].filter(parameter => !parametersToHide.includes(parameter)), []);
    }
    /**
     * Private method to build rural category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns rural category
     * @source
     */
    ruralBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const ruralProfiles = [
            LisioProfileNames.LOW_NETWORK,
        ].filter(profile => !profilesToHide.includes(profile));
        return new LisioCategory(LisioCategoryNames.RURAL, ruralProfiles, [].filter(parameter => !parametersToHide.includes(parameter)), []);
    }
    /**
     * Private method to build accessibility category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns accessibility category
     * @source
     */
    accessibilityBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const accessibilityProfileNames = [
            LisioProfileNames.DYSLEXIA,
            LisioProfileNames.GLASS_X2,
            LisioProfileNames.GLASS_X4,
            LisioProfileNames.EPILEPSY,
            LisioProfileNames.MOVEMENTS,
            LisioProfileNames.GESTURES,
            LisioProfileNames.FOCUS,
            LisioProfileNames.DICTIONARY,
            LisioProfileNames.DARK_MODE,
        ].filter(profile => !profilesToHide.includes(profile));
        const accessibilityParameterNames = [].filter(parameter => !parametersToHide.includes(parameter));
        const accessibilityCategoryNames = [
            LisioCategoryNames.DALTONISM,
        ].filter(subCategory => !subCategoriesToHide.includes(subCategory));
        return new LisioCategory(LisioCategoryNames.ACCESSIBILITY, accessibilityProfileNames, accessibilityParameterNames, accessibilityCategoryNames);
    }
    /**
     * Private method to build comfort category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns comfort category
     * @source
     */
    comfortBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const comfortProfileNames = [
            LisioProfileNames.READING,
            LisioProfileNames.VISION,
            LisioProfileNames.PRESBYOPIA,
            LisioProfileNames.CATARACT,
            LisioProfileNames.ATTENTION,
            LisioProfileNames.MEMORY,
            LisioProfileNames.VERY_LOW_EYE_SIGHT,
            LisioProfileNames.LEARNING,
            LisioProfileNames.LOW_EYE_SIGHT,
            LisioProfileNames.HEADACHE,
        ].filter(profile => !profilesToHide.includes(profile));
        const comfortParameteNames = [].filter(parameter => !parametersToHide.includes(parameter));
        const comfortCategoryNames = [
            LisioCategoryNames.DYSLEXIA_COMFORT,
        ].filter(subCategory => !subCategoriesToHide.includes(subCategory));
        return new LisioCategory(LisioCategoryNames.COMFORT, comfortProfileNames, comfortParameteNames, comfortCategoryNames);
    }
    /**
     * Private method to build speech synthesis category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns speech synthesis category
     * @source
     */
    speechSynthesisBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const speechSynthesisProfileNames = [].filter(profile => !profilesToHide.includes(profile));
        const speechSynthesisParameterNames = [
            LisioBooleanParameterNames.SPEECH_SYNTHESIS_STATE,
            LisioStringParameterNames.SPEECH_SYNTHESIS_VOICE,
            LisioNumericParameterNames.SPEECH_SYNTHESIS_PITCH,
            LisioNumericParameterNames.SPEECH_SYNTHESIS_RATE,
        ].filter(parameter => !parametersToHide.includes(parameter));
        return new LisioCategory(LisioCategoryNames.SPEECH_SYNTHESIS, speechSynthesisProfileNames, speechSynthesisParameterNames, []);
    }
    /**
     * Private method to build google translation category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns google translation category
     * @source
     */
    googleTranslationBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const googleTranslationProfileNames = [].filter(profile => !profilesToHide.includes(profile));
        const googleTranslationParameterNames = [
            LisioStringParameterNames.GOOGLE_TRANSLATION,
        ].filter(parameter => !parametersToHide.includes(parameter));
        return new LisioCategory(LisioCategoryNames.GOOGLE_TRANSLATION, googleTranslationProfileNames, googleTranslationParameterNames, []);
    }
    /**
     * Private method to build deepl translation category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns deepl translation category
     * @source
     */
    deeplTranslationBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const deeplTranslationProfileNames = [].filter(profile => !profilesToHide.includes(profile));
        const deeplTranslationParameterNames = [
            LisioStringParameterNames.DEEPL_TRANSLATION,
        ].filter(parameter => !parametersToHide.includes(parameter));
        return new LisioCategory(LisioCategoryNames.DEEPL_TRANSLATION, deeplTranslationProfileNames, deeplTranslationParameterNames, []);
    }
    /**
     * Private method to build dyslexia category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns dyslexia category
     * @source
     */
    dyslexiaBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const dyslexiaProfileNames = [
            LisioProfileNames.UNDERLINE,
        ].filter(profile => !profilesToHide.includes(profile));
        const dyslexiaParameterNames = [
            LisioStringParameterNames.UNDERLINE_RED,
            LisioStringParameterNames.UNDERLINE_GREEN,
            LisioStringParameterNames.UNDERLINE_BLUE,
        ].filter(parameter => !parametersToHide.includes(parameter));
        return new LisioCategory(LisioCategoryNames.DYSLEXIA_COMFORT, dyslexiaProfileNames, dyslexiaParameterNames, []);
    }
    /**
     * Private method to build daltonism category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns daltonism category
     * @source
     */
    daltonismBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const daltonismProfileNames = [].filter(profile => !profilesToHide.includes(profile));
        const daltonismParameterNames = [
            LisioStringParameterNames.DALTONISM,
        ].filter(parameter => !parametersToHide.includes(parameter));
        return new LisioCategory(LisioCategoryNames.DALTONISM, daltonismProfileNames, daltonismParameterNames, []);
    }
    /**
     * Private method to build settings category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Returns settings category
     * @source
     */
    settingsBuilder(subCategoriesToHide, profilesToHide, parametersToHide) {
        const settingsProfileNames = [].filter(profile => !profilesToHide.includes(profile));
        const settingsParameterNames = [
            LisioBooleanParameterNames.SHOW_ALT,
            LisioBooleanParameterNames.HIGHLIGHT,
            LisioBooleanParameterNames.DISABLE_ANIMATION,
            LisioBooleanParameterNames.KEYBOARD_NAVIGATION,
            LisioBooleanParameterNames.READING_MASK,
            LisioBooleanParameterNames.LIST,
            LisioNumericParameterNames.FONT_SIZE,
            LisioNumericParameterNames.LINE_HEIGHT,
            LisioNumericParameterNames.WORD_SPACING,
            LisioNumericParameterNames.LETTER_SPACING,
            LisioNumericParameterNames.CONTRAST,
            LisioNumericParameterNames.ZOOM,
            LisioNumericParameterNames.BIGGER_CLICK,
            // LisioNumericParameterNames.DALTONISM_R,
            // LisioNumericParameterNames.DALTONISM_G,
            // LisioNumericParameterNames.DALTONISM_B,
            // LisioNumericParameterNames.DALTONISM_HUE,
            // LisioNumericParameterNames.DALTONISM_SATURATION,
            // LisioNumericParameterNames.DALTONISM_BRIGHTNESS,
            LisioStringParameterNames.THEME,
            LisioStringParameterNames.FONT_FAMILY,
            LisioStringParameterNames.TEXT_ALIGN,
            LisioStringParameterNames.CURSOR_SIZE,
        ].filter(parameter => !parametersToHide.includes(parameter));
        return new LisioCategory(LisioCategoryNames.SETTINGS, settingsProfileNames, settingsParameterNames, []);
    }
    /**
     * Public method to build a desired category
     * @param {LisiocategoryNames} categoryName - Name of desired category
     * @param {LisioCategoryNames[]} subCategoriesToHide - Sub-categories to hide
     * @param {LisioProfileNames[]} profilesToHide - Profiles to hide
     * @param {LisioParameterNames[]} parametersToHide - Parameters to hide
     * @returns Retuns a lisio category
     * @throws If category doesn't exist.\
     * See {@link Src/Utils.LisioProfilErrorCodes.CATEGORY_NOT_FOUND | LisioProfilErrorCodes.CATEGORY_NOT_FOUND}
     * @source
     */
    buildLisioCategory(categoryName, subCategoriesToHide, profilesToHide, parametersToHide) {
        const builder = this._builders.get(categoryName);
        if (builder == undefined) {
            throw new Error(`Code : ${LisioProfilErrorCodes.CATEGORY_NOT_FOUND}. Profile "${categoryName}" not found`);
        }
        else {
            return builder(subCategoriesToHide, profilesToHide, parametersToHide);
        }
    }
    /**
     * Constructor of class {@link LisioCategoryFactory | LisioCategoryFactory}
     * @throws If singleton already initialize.\
     * See {@link https://env-preprod-docs.lisio.fr/lisio-engine/enums/Src_Core.CoreErrorCodes.html#SINGLETON_NOT_UNIQUE | CoreErrorCodes.SINGLETON_NOT_UNIQUE}
     * @source
     */
    constructor() {
        if (LisioCategoryFactory._current == undefined) {
            LisioCategoryFactory._current = this;
        }
        else {
            throw new Error(`Code : LisioCore_0. Singleton already initialize`);
        }
    }
}
export { LisioCategoryFactory };
